import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Package, Users, MessageSquare, Upload, Edit, Trash, Key, Lock, Star, Download, Mail } from 'lucide-react';
import { collection, getDocs, deleteDoc, doc, updateDoc, serverTimestamp, addDoc, getDoc, onSnapshot} from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from '../lib/firebase';
import type { Extension, User, Review } from '../types/index';
import toast from 'react-hot-toast';
import ExtensionFormModal from '../components/ExtensionFormModal';
import { Link } from 'react-router-dom';
import CategoryFormModal from '../components/CategoryFormModal';
import UserFormModal from '../components/UserFormModal';
import AdminReports from '../components/AdminReports';
import AdminEmailSettings from '../components/AdminContactMessages';
import Navbar from '../components/Navbar';

function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('extensions');
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState<Extension | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [reviews, setReviews] = useState<(Review & { extensionName: string })[]>([]);
  const [categories, setCategories] = useState<{id: string, name: string}[]>([]);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<{id: string, name: string} | null>(null);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);

  useEffect(() => {
    let unsubscribe: () => void;

    const fetchData = async () => {
      try {
        const extensionsRef = collection(db, 'extensions');
        unsubscribe = onSnapshot(extensionsRef, (snapshot) => {
          const extensionsList: Extension[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            extensionsList.push({
              id: doc.id,
              name: data.name,
              description: data.description,
              version: data.version,
              author: data.author,
              downloadUrl: data.downloadUrl,
              thumbnailUrl: data.thumbnailUrl,
              categoryId: data.categoryId,
              category: data.category,
              downloads: data.downloads || 0,
              totalRatings: data.totalRatings || 0,
              totalRatingSum: data.totalRatingSum || 0,
              averageRating: data.totalRatings > 0 ? data.totalRatingSum / data.totalRatings : 0,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date()
            });
          });
          setExtensions(extensionsList);
          setLoading(false);
        });
      } catch (error) {
        toast.error('Failed to load extensions');
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'reviews') {
      fetchReviews();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'categories') {
      const categoriesRef = collection(db, 'categories');
      const unsubscribe = onSnapshot(categoriesRef, (snapshot) => {
        const categoryList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));
        setCategories(categoryList);
      });

      return () => unsubscribe();
    }
  }, [activeTab]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesRef = collection(db, 'categories');
        const snapshot = await getDocs(categoriesRef);
        const categoriesList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));
        setCategories(categoriesList);
      } catch (error) {
        toast.error('Failed to load categories');
      }
    };

    fetchCategories();
  }, []);

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      // Use a Map to prevent duplicates, using uid as key
      const usersMap = new Map();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Only add if user doesn't exist in map or if this is a more recent update
        const existingUser = usersMap.get(doc.id);
        const currentUpdatedAt = data.updatedAt?.toDate();
        
        if (!existingUser || (currentUpdatedAt && (!existingUser.updatedAt || currentUpdatedAt > existingUser.updatedAt))) {
          usersMap.set(doc.id, {
            uid: doc.id,
            email: data.email,
            displayName: data.displayName || 'User',
            photoURL: data.photoURL || '',
            role: data.role || 'user',
            suspended: data.suspended || false,
            emailVerified: data.emailVerified || false,
            createdAt: data.createdAt?.toDate(),
            updatedAt: currentUpdatedAt
          });
        }
      });

      // Convert Map to array and sort
      const usersList = Array.from(usersMap.values()).sort((a, b) => {
        // First sort by role (admins first)
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (a.role !== 'admin' && b.role === 'admin') return 1;
        
        // Then by verified status
        if (a.emailVerified && !b.emailVerified) return -1;
        if (!a.emailVerified && b.emailVerified) return 1;
        
        // Finally by display name
        return (a.displayName || '').localeCompare(b.displayName || '');
      });

      setUsers(usersList);
    } catch (error: any) {
      toast.error('Failed to load users');
    }
  };

  const fetchReviews = async () => {
    try {
      const reviewsRef = collection(db, 'reviews');
      const reviewsSnapshot = await getDocs(reviewsRef);
      
      const reviewsWithExtensions = await Promise.all(
        reviewsSnapshot.docs.map(async (reviewDoc) => {
          const reviewData = reviewDoc.data();
          const extensionRef = doc(db, 'extensions', reviewData.extensionId);
          const extensionDoc = await getDoc(extensionRef);
          const extensionData = extensionDoc.data();

          // Get user data to get the icon
          const userRef = doc(db, 'users', reviewData.userId);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.data();
          
          return {
            id: reviewDoc.id,
            userId: reviewData.userId,
            userDisplayName: reviewData.userDisplayName,
            userPhotoURL: reviewData.userPhotoURL || '',
            userIcon: userData?.icon || '👤',  // Add userIcon with default
            extensionId: reviewData.extensionId,
            rating: reviewData.rating,
            comment: reviewData.comment,
            createdAt: reviewData.createdAt,
            extensionName: extensionData?.name || 'Unknown Extension'
          };
        })
      );

      setReviews(reviewsWithExtensions);
    } catch (error) {
      toast.error('Failed to load reviews');
    }
  };

  const handleDeleteExtension = async (extensionId: string) => {
    if (!window.confirm('Are you sure you want to delete this extension?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'extensions', extensionId));
      setExtensions(extensions.filter(ext => ext.id !== extensionId));
      toast.success('Extension deleted successfully');
    } catch (error) {
      toast.error('Failed to delete extension');
    }
  };

  const handleEditClick = (extension: Extension) => {
    setSelectedExtension(extension);
    setIsEditModalOpen(true);
  };

  const handleResetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent');
    } catch (error) {
      toast.error('Failed to send reset email');
    }
  };

  const handleToggleSuspend = async (userId: string, isSuspended: boolean) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        suspended: !isSuspended,
        updatedAt: serverTimestamp()
      });
      
      // Send notification
      await addDoc(collection(db, 'notifications'), {
        userId,
        type: isSuspended ? 'ACCOUNT_UNSUSPENDED' : 'ACCOUNT_SUSPENDED',
        message: isSuspended 
          ? 'Your account has been unsuspended.' 
          : 'Your account has been suspended.',
        createdAt: serverTimestamp(),
        read: false
      });

      fetchUsers();
      toast.success(`User ${isSuspended ? 'unsuspended' : 'suspended'} successfully`);
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', userId));
      
      // Send notification
      await addDoc(collection(db, 'notifications'), {
        userId,
        type: 'ACCOUNT_DELETED',
        message: 'Your account has been deleted by an administrator.',
        createdAt: serverTimestamp(),
        read: false
      });

      fetchUsers();
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const reviewRef = doc(db, 'reviews', reviewId);
      const reviewDoc = await getDoc(reviewRef);
      const reviewData = reviewDoc.data();

      if (reviewData) {
        // Delete the review
        await deleteDoc(reviewRef);

        // Update extension stats
        const extensionRef = doc(db, 'extensions', reviewData.extensionId);
        const extensionDoc = await getDoc(extensionRef);
        const extensionData = extensionDoc.data();

        if (extensionData) {
          const newTotalRatings = (extensionData.totalRatings || 0) - 1;
          const newTotalSum = (extensionData.totalRatingSum || 0) - reviewData.rating;
          const newAverage = newTotalRatings > 0 
            ? Math.round((newTotalSum / newTotalRatings) * 10) / 10 
            : 0;

          await updateDoc(extensionRef, {
            totalRatings: newTotalRatings,
            totalRatingSum: newTotalSum,
            averageRating: newAverage,
            updatedAt: serverTimestamp()
          });

          // Send notification to user
          await addDoc(collection(db, 'notifications'), {
            userId: reviewData.userId,
            type: 'REVIEW_DELETED',
            message: 'Your review has been removed by an administrator.',
            createdAt: serverTimestamp(),
            read: false
          });
        }

        setReviews(reviews.filter(review => review.id !== reviewId));
        toast.success('Review deleted successfully');
      }
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const resetDownloadCount = async (extensionId: string) => {
    if (!window.confirm('Are you sure you want to reset the download count?')) {
      return;
    }

    try {
      const extensionRef = doc(db, 'extensions', extensionId);
      await updateDoc(extensionRef, {
        downloads: 0,
        updatedAt: serverTimestamp()
      });
      toast.success('Download count reset');
    } catch (error) {
      toast.error('Failed to reset download count');
    }
  };

  const handleAddCategory = async (name: string) => {
    try {
      await addDoc(collection(db, 'categories'), {
        name,
        createdAt: serverTimestamp()
      });
      toast.success('Category added successfully');
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const handleUpdateCategory = async (id: string, name: string) => {
    try {
      await updateDoc(doc(db, 'categories', id), {
        name,
        updatedAt: serverTimestamp()
      });
      toast.success('Category updated successfully');
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'categories', id));
      toast.success('Category deleted successfully');
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleSearch = (query: string) => {
    // Navigate to home page with search query
    navigate(`/?search=${encodeURIComponent(query)}`);
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
        <p className="text-gray-600 mt-2">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <>
      <Navbar onSearch={handleSearch} />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Admin Dashboard</h1>
        
        <div className="flex space-x-4 mb-8">
          <button
            onClick={() => setActiveTab('extensions')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              activeTab === 'extensions'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Package className="w-5 h-5" />
            <span>Extensions</span>
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              activeTab === 'users'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Users className="w-5 h-5" />
            <span>Users</span>
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              activeTab === 'reviews'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <MessageSquare className="w-5 h-5" />
            <span>Reviews</span>
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              activeTab === 'categories'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Package className="w-5 h-5" />
            <span>Categories</span>
          </button>
          <button
            onClick={() => setActiveTab('emails')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              activeTab === 'emails'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Mail className="w-5 h-5" />
            <span>Contact Emails</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          {activeTab === 'extensions' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Manage Extensions</h2>
                <button
                  onClick={() => setIsUploadModalOpen(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Upload className="w-5 h-5" />
                  <span>Upload New</span>
                </button>
              </div>
              
              {loading ? (
                <div className="text-center py-4">Loading extensions...</div>
              ) : (
                <div className="overflow-x-auto rounded-xl shadow-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Author</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Downloads</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {extensions.map((extension) => (
                        <tr key={extension.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">{extension.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{extension.author}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{extension.downloads}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{extension.category}</td>
                          <td className="px-6 py-4 whitespace-nowrap space-x-4">
                            <button 
                              onClick={() => handleEditClick(extension)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => resetDownloadCount(extension.id)}
                              className="text-yellow-600 hover:text-yellow-900"
                              title="Reset Downloads"
                            >
                              <Download className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteExtension(extension.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Manage Users</h2>
                <button
                  onClick={() => setIsCreateUserModalOpen(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Upload className="w-5 h-5" />
                  <span>Create User</span>
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.uid}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {user.photoURL ? (
                              <img src={user.photoURL} alt="" className="h-10 w-10 rounded-full" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-xl font-bold text-gray-600">
                                  {user.displayName[0]}
                                </span>
                              </div>
                            )}
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.displayName}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.suspended ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {user.suspended ? 'Suspended' : 'Active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap space-x-4">
                          <button
                            onClick={() => handleResetPassword(user.email)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Reset Password"
                          >
                            <Key className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleToggleSuspend(user.uid, user.suspended || false)}
                            className={`${user.suspended ? 'text-green-600 hover:text-green-900' : 'text-yellow-600 hover:text-yellow-900'}`}
                            title={user.suspended ? 'Unsuspend User' : 'Suspend User'}
                          >
                            <Lock className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.uid)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete User"
                          >
                            <Trash className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="space-y-8">
              <AdminReports />
              
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">All Reviews</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Extension</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comment</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reviews.map((review) => (
                        <tr key={review.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {review.userPhotoURL ? (
                                <img src={review.userPhotoURL} alt="" className="h-8 w-8 rounded-full" />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                  <span className="text-sm font-medium text-gray-600">
                                    {review.userDisplayName[0]}
                                  </span>
                                </div>
                              )}
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">{review.userDisplayName}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link to={`/extension/${review.extensionId}`} className="text-indigo-600 hover:text-indigo-900">
                              {review.extensionName}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex text-yellow-400">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${i < review.rating ? 'fill-current' : 'text-gray-300'}`}
                                />
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{review.comment}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {review.createdAt.toDate().toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleDeleteReview(review.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Review"
                            >
                              <Trash className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Manage Categories</h2>
                <button
                  onClick={() => setIsAddCategoryModalOpen(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Upload className="w-5 h-5" />
                  <span>Add Category</span>
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {categories.map((category) => (
                      <tr key={category.id}>
                        <td className="px-6 py-4 whitespace-nowrap">{category.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap space-x-4">
                          <button
                            onClick={() => {
                              setEditingCategory(category);
                              setIsAddCategoryModalOpen(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'emails' && (
            <AdminEmailSettings />
          )}
        </div>

        <ExtensionFormModal
          isOpen={isUploadModalOpen || isEditModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setIsEditModalOpen(false);
            setSelectedExtension(null);
          }}
          onSuccess={(updatedExtension) => {
            setExtensions(prev => prev.map(ext => 
              ext.id === updatedExtension.id ? updatedExtension : ext
            ));
          }}
          extension={selectedExtension}
          categories={categories}
        />

        <CategoryFormModal
          isOpen={isAddCategoryModalOpen}
          onClose={() => {
            setIsAddCategoryModalOpen(false);
            setEditingCategory(null);
          }}
          onSubmit={async (name) => {
            if (editingCategory) {
              await handleUpdateCategory(editingCategory.id, name);
            } else {
              await handleAddCategory(name);
            }
          }}
          category={editingCategory}
        />

        <UserFormModal
          isOpen={isCreateUserModalOpen}
          onClose={() => setIsCreateUserModalOpen(false)}
        />
      </div>
    </>
  );
}

export default AdminDashboard;